import { useCallback } from "react";
import { WarningCircle, Barcode } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function getStockBadge(stockReal, stockPrevisto, alerta) {
  if (stockReal <= 0) return <span className="badge-out">Agotado</span>;
  if (alerta) return <span className="badge-low">Bajo</span>;
  return <span className="badge-ok">OK</span>;
}

export default function InventoryTable({ inventory, categories = [], onUpdateStockPrevisto, onUpdateCategory }) {
  const handleBlur = useCallback((articulo) => (e) => {
    onUpdateStockPrevisto(articulo, e.target.value);
  }, [onUpdateStockPrevisto]);

  const handleCategoryChange = useCallback((articulo) => (value) => {
    onUpdateCategory(articulo, value);
  }, [onUpdateCategory]);

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Artículo</TableHead>
            <TableHead>Código</TableHead>
            <TableHead>Categoría</TableHead>
            <TableHead className="text-right">Comprado</TableHead>
            <TableHead className="text-right">Consumido</TableHead>
            <TableHead className="text-right">Stock Real</TableHead>
            <TableHead className="text-right">Stock Previsto</TableHead>
            <TableHead className="text-right">Precio Prom.</TableHead>
            <TableHead className="text-center">Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inventory.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center text-slate-500 py-8">
                No hay artículos en el inventario
              </TableCell>
            </TableRow>
          ) : (
            inventory.map((item) => (
              <TableRow 
                key={item.articulo} 
                className={`table-row ${item.alerta_stock ? "bg-yellow-50" : ""}`}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {item.alerta_stock && (
                      <WarningCircle size={16} className="text-yellow-600" />
                    )}
                    {item.articulo}
                  </div>
                </TableCell>
                <TableCell>
                  {item.codigo_barras ? (
                    <span className="flex items-center gap-1 text-xs text-slate-500 font-mono">
                      <Barcode size={14} />
                      {item.codigo_barras}
                    </span>
                  ) : (
                    <span className="text-slate-300">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <Select 
                    value={item.categoria || "Otros"} 
                    onValueChange={handleCategoryChange(item.articulo)}
                  >
                    <SelectTrigger 
                      data-testid={`category-select-${item.articulo}`}
                      className="w-[130px] h-8 text-xs"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.nombre}>{cat.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">{item.cantidad_comprada}</TableCell>
                <TableCell className="text-right">{item.cantidad_consumida}</TableCell>
                <TableCell className="text-right font-semibold">{item.stock_real}</TableCell>
                <TableCell className="text-right">
                  <Input
                    data-testid={`stock-previsto-${item.articulo}`}
                    type="number"
                    step="0.01"
                    className="w-20 text-right inline-block"
                    defaultValue={item.stock_previsto}
                    onBlur={handleBlur(item.articulo)}
                  />
                </TableCell>
                <TableCell className="text-right">{item.precio_promedio.toFixed(2)}€</TableCell>
                <TableCell className="text-center">
                  {getStockBadge(item.stock_real, item.stock_previsto, item.alerta_stock)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
